import { getTwilioClient } from '@src/lib/twilio-chat/twilio-chat';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

export const createChatChannel = async (channelId, options) => {
    const { onMessageAdded, onConnectedToChannel = () => {} } = options || {};
    try {
        const client = await getTwilioClient();

        // Wait for client to be fully ready
        if (client.connectionState !== 'connected') {
            await new Promise(resolve => {
                if (client.connectionState === 'connected') {
                    resolve();
                } else {
                    client.on('connectionStateChanged', state => {
                        if (state === 'connected') {
                            resolve();
                        }
                    });
                }
            });
        }

        const channel = await client.getChannelBySid(channelId);

        // Set up message listener immediately - before join/connection
        channel.removeAllListeners('messageAdded');

        const setupListener = () => {
            channel.on('messageAdded', ({ state }) => {
                if (onMessageAdded) {
                    onMessageAdded({
                        Guid: '',
                        SID: state.sid,
                        Message: state.body,
                        SentDateTime: moment(state.timestamp).unix(),
                        Sender: state.author,
                        Recipient: ''
                    });
                }
            });
        };

        // Ensure user is joined
        if (channel.status !== 'joined') {
            await channel
                .join()
                .then(async () => {
                    // For new users, force proper event subscription
                    try {
                        // Force channel to sync and subscribe to events
                        await channel.updateLastConsumedMessageIndex(null);

                        // Ensure we're properly registered as channel member
                        await channel.getMembers();

                        // Set up listener after ensuring proper subscription
                        setupListener();
                    } catch (syncError) {
                        // Set up listener anyway if sync fails
                        setupListener();
                    }

                    // Delay before calling onConnectedToChannel
                    setTimeout(() => {
                        onConnectedToChannel();
                    }, 1000);
                })
                .catch(function(err) {
                    console.error("Couldn't join channel ", err);
                });
        } else {
            setupListener();
            onConnectedToChannel();
        }

        return channel;
    } catch (error) {
        console.error('Error initializing chat:', error);
        return null;
    }
};

export const useCreateChatChannel = (channelId, options) =>
    useQuery({
        queryKey: ['connect-chat', channelId],
        queryFn: async () => createChatChannel(channelId, options),
        refetchOnMount: false,
        refetchOnWindowFocus: false
    });
